import { useMemo, useCallback, useEffect, useRef, useState } from "react";
import { useAccountant } from "./AccountantContext";
import FilterBar from "./FilterBar";
import EditableCell from "./EditableCell";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableFooter from "@mui/material/TableFooter";
import TablePagination from "@mui/material/TablePagination";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import { getJobsPricing, updateJobPricing, issueInvoice, isInvoiceInProgress, saveInvoicePDF, deleteJobFromInvoice } from "../../service/PricingService";
import fileDownload from "js-file-download";
import { JOB_TYPE_OPTIONS } from "./jobTypeOptions";

const EDITABLE_NUMBER_COLS = [
    { key: 'total_override_price', label: 'Total Price' },
    { key: 'labour_price', label: 'Labour' },
    { key: 'labour_override_price', label: 'Team Price' },
    { key: 'linens_price', label: 'Linens' },
    { key: 'consumable_price', label: 'Consumable' },
    { key: 'extra_linens_price', label: 'Extra Linens' },
    { key: 'extra_price', label: 'Cleaner Extra' },
    { key: 'call_out_price', label: 'Call Out' },
    { key: 'key_delivery_price', label: 'Key Delivery' },
    { key: 'key_return_price', label: 'Key Return' },
    { key: 'labour_surcharge_price', label: 'Agent Labour Surcharge' },
    { key: 'carpet_cleaning_price', label: 'Carpet Cleaning' },
    { key: 'credit_price', label: 'Credit' },
    { key: 'parking_price', label: 'Parking' },
    { key: 'long_distance_price', label: 'Long Distance' },
];

const titleStyle = {
    fontWeight: 900,
    fontSize: 20,
    mb: 1,
    mt: 2,
    pl: 2,
    color: 'primary.main',
};

const headCellSx = { fontWeight: 500, whiteSpace: 'nowrap', fontSize: 13, fontStyle: 'italic' };
const bodyCellSx = { fontSize: 13, py: 0.5, whiteSpace: 'nowrap' };
const truncatedCellSx = { ...bodyCellSx, maxWidth: 200, overflow: 'hidden', textOverflow: 'clip' };
const agentCellSx = { ...bodyCellSx, maxWidth: 120, overflow: 'hidden', textOverflow: 'clip' };
const priceHeadSx = { ...headCellSx, width: 80, minWidth: 80, maxWidth: 80, overflow: 'hidden', textOverflow: 'clip' };
const priceCellSx = { ...bodyCellSx, width: 80, minWidth: 80, maxWidth: 80 };
const paginationSx = { '& .MuiTablePagination-toolbar': { alignItems: 'baseline' } };

function isWorkOrderPricingRow(row) {
    return String(row?.job_type || '').toLowerCase() === 'task';
}

async function responseError(response, fallback) {
    if (response.ok) return null;
    const body = await response.json().catch(() => null);
    return new Error(body?.message || fallback);
}

export default function JobInvoiceTab() {
    const [state, dispatch] = useAccountant();
    const pollingRef = useRef(null);
    const unmountedRef = useRef(false);
    const [notInvoicedPage, setNotInvoicedPage] = useState(0);
    const [notInvoicedRowsPerPage, setNotInvoicedRowsPerPage] = useState(20);
    const [invoicedPage, setInvoicedPage] = useState(0);
    const [invoicedRowsPerPage, setInvoicedRowsPerPage] = useState(20);
    const [jobTypeFilter, setJobTypeFilter] = useState('');
    const [bulkDelete, setBulkDelete] = useState(false);

    const notInvoiced = useMemo(() => {
        return state.jobPrice
            .filter(it => it.full_address?.toLowerCase().includes(state.searchAddress.toLowerCase()))
            .filter(it => !state.searchJobId || String(it.cos_job_id).includes(String(state.searchJobId)))
            .filter(it => !it.invoice_number || it.invoice_number.length === 0)
            .filter(it => it.agent_name ? it.agent_name.toLowerCase().includes(state.searchAgent.toLowerCase()) : state.searchAgent === '')
            .filter(it => !jobTypeFilter || it.job_type === jobTypeFilter);
    }, [state.jobPrice, state.searchAddress, state.searchJobId, state.searchAgent, jobTypeFilter]);

    const invoiced = useMemo(() => {
        return state.jobPrice
            .filter(it => it.full_address?.toLowerCase().includes(state.searchAddress.toLowerCase()))
            .filter(it => !state.searchJobId || String(it.cos_job_id).includes(String(state.searchJobId)))
            .filter(it => it.invoice_number && it.invoice_number.length > 0)
            .filter(it => it.agent_name ? it.agent_name.toLowerCase().includes(state.searchAgent.toLowerCase()) : state.searchAgent === '')
            .filter(it => !jobTypeFilter || it.job_type === jobTypeFilter);
    }, [state.jobPrice, state.searchAddress, state.searchJobId, state.searchAgent, jobTypeFilter]);

    const refreshJobs = useCallback(() => {
        dispatch({ type: 'SET_LOADING', payload: true });
        dispatch({ type: 'SET_NOTIFICATION', payload: null });
        getJobsPricing(state.filterStartDate, state.filterEndDate, state.filterRegion)
            .then(res => { if (!res.ok) throw new Error('Failed to load job pricing'); return res.json(); })
            .then(data => {
                dispatch({ type: 'SET_JOB_PRICING', payload: data });
                dispatch({ type: 'SET_LOADING', payload: false });
            })
            .catch(err => {
                dispatch({ type: 'SET_LOADING', payload: false });
                dispatch({ type: 'SET_NOTIFICATION', payload: { message: err.message, isSuccess: false } });
            });
    }, [dispatch, state.filterStartDate, state.filterEndDate, state.filterRegion]);

    const handleUpdateValue = useCallback((cosJobId, cell, oldValue, value) => {
        dispatch({ type: 'UPDATE_JOB_CELL', payload: { cos_job_id: cosJobId, cell, value } });
        dispatch({ type: 'SET_LOADING', payload: true });
        updateJobPricing({ cos_job_id: cosJobId, [cell]: value })
            .then(() => dispatch({ type: 'SET_LOADING', payload: false }))
            .catch(err => {
                dispatch({ type: 'UPDATE_JOB_CELL', payload: { cos_job_id: cosJobId, cell, value: oldValue } });
                dispatch({ type: 'SET_LOADING', payload: false });
                dispatch({ type: 'SET_NOTIFICATION', payload: { message: err.message, isSuccess: false } });
            });
    }, [dispatch]);

    const startPolling = useCallback(() => {
        if (pollingRef.current) return;
        pollingRef.current = setInterval(() => {
            isInvoiceInProgress()
                .then(res => res.json())
                .then(inProgress => {
                    if (unmountedRef.current) return;
                    if (!inProgress) {
                        clearInterval(pollingRef.current);
                        pollingRef.current = null;
                        dispatch({ type: 'SET_INVOICE_IN_PROGRESS', payload: false });
                        refreshJobs();
                    }
                })
                .catch(() => {
                    if (unmountedRef.current) return;
                    clearInterval(pollingRef.current);
                    pollingRef.current = null;
                    dispatch({ type: 'SET_INVOICE_IN_PROGRESS', payload: false });
                    dispatch({ type: 'SET_NOTIFICATION', payload: { message: 'Lost connection while checking invoice status', isSuccess: false } });
                });
        }, 5000);
    }, [dispatch, refreshJobs]);

    useEffect(() => {
        return () => {
            unmountedRef.current = true;
            if (pollingRef.current) clearInterval(pollingRef.current);
        };
    }, []);

    const handleIssueInvoice = useCallback(() => {
        dispatch({ type: 'SET_LOADING', payload: true });
        dispatch({ type: 'SET_INVOICE_IN_PROGRESS', payload: true });
        dispatch({ type: 'SET_NOTIFICATION', payload: null });
        issueInvoice(notInvoiced, state.invoiceGroupType)
            .then(res => {
                if (!res.ok) return res.json().then(e => { throw new Error(e.message); });
                dispatch({ type: 'SET_LOADING', payload: false });
                startPolling();
            })
            .catch(err => {
                dispatch({ type: 'SET_LOADING', payload: false });
                dispatch({ type: 'SET_INVOICE_IN_PROGRESS', payload: false });
                dispatch({ type: 'SET_NOTIFICATION', payload: { message: err.message, isSuccess: false } });
            });
    }, [dispatch, notInvoiced, state.invoiceGroupType, startPolling]);

    const handleSavePDF = useCallback((cosJobId, invoiceNumber) => {
        dispatch({ type: 'SET_LOADING', payload: true });
        saveInvoicePDF(cosJobId)
            .then(res => { if (!res.ok) throw new Error('Failed to download PDF'); return res.blob(); })
            .then(blob => { fileDownload(blob, invoiceNumber + '.pdf'); dispatch({ type: 'SET_LOADING', payload: false }); })
            .catch(err => {
                dispatch({ type: 'SET_LOADING', payload: false });
                dispatch({ type: 'SET_NOTIFICATION', payload: { message: err.message, isSuccess: false } });
            });
    }, [dispatch]);

    const handleDeleteJob = useCallback(async (row) => {
        const deleteEntireInvoice = bulkDelete;
        if (deleteEntireInvoice && !window.confirm(
            `Delete invoice ${row.invoice_number} and return all linked jobs to Not Invoiced?\n\n` +
            'This includes linked jobs outside the current date range. This action cannot be undone.'
        )) return;

        dispatch({ type: 'SET_LOADING', payload: true });
        dispatch({ type: 'SET_NOTIFICATION', payload: null });
        try {
            const response = await deleteJobFromInvoice(row.cos_job_id, deleteEntireInvoice);
            const error = await responseError(
                response,
                deleteEntireInvoice ? 'Failed to delete invoice' : 'Failed to remove job from invoice'
            );
            if (error) throw error;
            if (deleteEntireInvoice) setBulkDelete(false);
            refreshJobs();
        } catch (error) {
            dispatch({ type: 'SET_LOADING', payload: false });
            dispatch({ type: 'SET_NOTIFICATION', payload: { message: error.message, isSuccess: false } });
        }
    }, [bulkDelete, dispatch, refreshJobs]);

    const renderEditablePriceCell = useCallback((row, col) => {
        const isWorkOrderRow = isWorkOrderPricingRow(row);
        const isTaskEditableField = col.key === 'total_override_price' || col.key === 'labour_override_price';

        if (isWorkOrderRow && !isTaskEditableField) {
            return <TableCell key={col.key} sx={priceCellSx} />;
        }

        return (
            <TableCell key={col.key} sx={priceCellSx}>
                <EditableCell
                    value={row[col.key]}
                    type="number"
                    disabled={state.isInvoiceInProgress}
                    onChange={(val) => handleUpdateValue(row.cos_job_id, col.key, row[col.key], val)}
                />
            </TableCell>
        );
    }, [handleUpdateValue, state.isInvoiceInProgress]);

    return (
        <div>
            <FilterBar
                showJobIdFilter
                showRegionSelector
                showInvoiceButton
                showJobTypeFilter
                jobTypeOptions={JOB_TYPE_OPTIONS}
                jobTypeFilter={jobTypeFilter}
                onJobTypeFilterChange={(value) => {
                    setJobTypeFilter(value);
                    setNotInvoicedPage(0);
                    setInvoicedPage(0);
                }}
                onRefresh={refreshJobs}
                onIssueInvoice={handleIssueInvoice}
            />

            <Typography sx={titleStyle}>Not Invoiced</Typography>
            <TableContainer>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headCellSx}>Job Id</TableCell>
                            <TableCell sx={{ ...headCellSx, minWidth: 180 }}>Address</TableCell>
                            <TableCell sx={headCellSx}>Agent Name</TableCell>
                            <TableCell sx={headCellSx}>Job Date</TableCell>
                            {EDITABLE_NUMBER_COLS.map(col => (
                                <TableCell key={col.key} sx={priceHeadSx} title={col.label}>{col.label}</TableCell>
                            ))}
                            <TableCell sx={{ ...headCellSx, minWidth: 140 }}>Comments</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {notInvoiced.slice(notInvoicedPage * notInvoicedRowsPerPage, notInvoicedPage * notInvoicedRowsPerPage + notInvoicedRowsPerPage).map(row => (
                            <TableRow key={row.cos_job_id} hover>
                                <TableCell sx={bodyCellSx}>
                                    {row.cos_job_id}
                                    {isWorkOrderPricingRow(row) ? " (Task)" : ""}
                                </TableCell>
                                <TableCell sx={truncatedCellSx} title={row.full_address}>{row.full_address}</TableCell>
                                <TableCell sx={agentCellSx} title={row.agent_name}>{row.agent_name}</TableCell>
                                <TableCell sx={bodyCellSx}>{row.job_date}</TableCell>
                                {EDITABLE_NUMBER_COLS.map(col => renderEditablePriceCell(row, col))}
                                <TableCell sx={bodyCellSx}>
                                    <EditableCell value={row.comments} type="text" disabled={state.isInvoiceInProgress} onChange={(val) => handleUpdateValue(row.cos_job_id, 'comments', row.comments, val)} />
                                </TableCell>
                            </TableRow>
                        ))}
                        {notInvoiced.length === 0 && (
                            <TableRow><TableCell colSpan={4 + EDITABLE_NUMBER_COLS.length + 1} align="center" sx={{ py: 4, color: 'text.secondary' }}>No data to display</TableCell></TableRow>
                        )}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                count={notInvoiced.length}
                                page={notInvoicedPage}
                                onPageChange={(_, p) => setNotInvoicedPage(p)}
                                rowsPerPage={notInvoicedRowsPerPage}
                                onRowsPerPageChange={(e) => { setNotInvoicedRowsPerPage(parseInt(e.target.value, 10)); setNotInvoicedPage(0); }}
                                rowsPerPageOptions={[20, 50, 100]}
                                labelDisplayedRows={({ count }) => `${count} total`}
                                sx={paginationSx}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>

            <Divider sx={{ my: 1 }} />

            <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 2, mb: 1, pl: 2 }}>
                <Typography sx={{ ...titleStyle, mt: 0, mb: 0, pl: 0 }}>Invoiced</Typography>
                <FormControlLabel
                    control={(
                        <Switch
                            checked={bulkDelete}
                            onChange={(event) => setBulkDelete(event.target.checked)}
                            disabled={state.loading || state.isInvoiceInProgress}
                        />
                    )}
                    label="Bulk delete invoice"
                />
            </Stack>
            <TableContainer>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headCellSx}>Job Id</TableCell>
                            <TableCell sx={{ ...headCellSx, minWidth: 180 }}>Address</TableCell>
                            <TableCell sx={headCellSx}>Agent Name</TableCell>
                            <TableCell sx={headCellSx}>Job Date</TableCell>
                            <TableCell sx={headCellSx}>Invoice Number</TableCell>
                            <TableCell sx={headCellSx}>Agent Total Price</TableCell>
                            <TableCell sx={headCellSx}>Comments</TableCell>
                            <TableCell sx={headCellSx}>Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {invoiced.slice(invoicedPage * invoicedRowsPerPage, invoicedPage * invoicedRowsPerPage + invoicedRowsPerPage).map(row => (
                            <TableRow key={row.cos_job_id} hover>
                                <TableCell sx={bodyCellSx}>{row.cos_job_id}</TableCell>
                                <TableCell sx={truncatedCellSx} title={row.full_address}>{row.full_address}</TableCell>
                                <TableCell sx={agentCellSx} title={row.agent_name}>{row.agent_name}</TableCell>
                                <TableCell sx={bodyCellSx}>{row.job_date}</TableCell>
                                <TableCell sx={bodyCellSx}>
                                    <Link href="#" onClick={(e) => { e.preventDefault(); handleSavePDF(row.cos_job_id, row.invoice_number); }}>
                                        {row.invoice_number}
                                    </Link>
                                </TableCell>
                                <TableCell sx={bodyCellSx}>{row.agent_total_price}</TableCell>
                                <TableCell sx={bodyCellSx}>{row.comments}</TableCell>
                                <TableCell sx={bodyCellSx}>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        disabled={state.loading || state.isInvoiceInProgress}
                                        aria-label={bulkDelete
                                            ? `Delete entire invoice ${row.invoice_number}`
                                            : `Remove job ${row.cos_job_id} from invoice`}
                                        title={bulkDelete
                                            ? `Delete entire invoice ${row.invoice_number}`
                                            : `Remove job ${row.cos_job_id} from invoice`}
                                        onClick={() => handleDeleteJob(row)}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        {invoiced.length === 0 && (
                            <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>No data to display</TableCell></TableRow>
                        )}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                count={invoiced.length}
                                page={invoicedPage}
                                onPageChange={(_, p) => setInvoicedPage(p)}
                                rowsPerPage={invoicedRowsPerPage}
                                onRowsPerPageChange={(e) => { setInvoicedRowsPerPage(parseInt(e.target.value, 10)); setInvoicedPage(0); }}
                                rowsPerPageOptions={[20, 50, 100]}
                                labelDisplayedRows={({ from, to, count }) => `${count} total`}
                                sx={paginationSx}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </div>
    );
}
